import { z } from "zod"

import { brandSnapshotSchema, invoiceTemplateSchema } from "./branding.ts"
import { abnRequestSchema, customerSchema } from "./customers.ts"
import { paymentMethodSchema, paymentSchema } from "./payments.ts"

/**
 * `invoices`, `invoice_lines` and the seller profile (T412, PRD R2). Money is integer minor
 * units plus `currency`. Not sync entities. Request schemas strict (lengths, enums); stored and
 * response schemas lenient (no `.max()`, defaults) because the server parses every stored row
 * with them. Each enum below is the one source for the DB CHECK lists (apps/web schema.test.ts).
 */

export const INVOICE_DOCUMENT_TITLES = ["tax_invoice", "invoice"] as const
export const invoiceDocumentTitleSchema = z.enum(INVOICE_DOCUMENT_TITLES)
export type InvoiceDocumentTitleValue = z.infer<typeof invoiceDocumentTitleSchema>

/** Stored lifecycle. Numbered invoices are voided, never deleted. */
export const INVOICE_STATUSES = ["draft", "sent", "void"] as const
export const invoiceStatusSchema = z.enum(INVOICE_STATUSES)
export type InvoiceStatus = z.infer<typeof invoiceStatusSchema>

export const INVOICE_SEND_STATUSES = ["none", "queued", "sending", "sent", "failed"] as const
export const invoiceSendStatusSchema = z.enum(INVOICE_SEND_STATUSES)
export type InvoiceSendStatus = z.infer<typeof invoiceSendStatusSchema>

export const INVOICE_AMOUNTS_ARE = ["inclusive", "exclusive"] as const
export const invoiceAmountsAreSchema = z.enum(INVOICE_AMOUNTS_ARE)

export const INVOICE_GST_ROUNDINGS = ["per-line", "per-document"] as const
export const invoiceGstRoundingSchema = z.enum(INVOICE_GST_ROUNDINGS)

export const INVOICE_TAX_CODES = ["GST", "GST_FREE"] as const
export const invoiceTaxCodeSchema = z.enum(INVOICE_TAX_CODES)

/** Currencies a new invoice may use; every value must be a core money `CurrencyCode`. */
export const INVOICE_CURRENCIES = ["AUD"] as const
export const invoiceCurrencySchema = z.enum(INVOICE_CURRENCIES)

export const INVOICE_SEND_OUTCOMES = ["sent", "failed"] as const
export const invoiceSendOutcomeSchema = z.enum(INVOICE_SEND_OUTCOMES)

/** Derived status shown in lists (never stored; see `deriveInvoiceStatus` in the web app). */
export const INVOICE_DISPLAY_STATUSES = [
  "draft",
  "not_sent",
  "sending",
  "sent",
  "partially_paid",
  "paid",
  "overdue",
  "void",
] as const
export const invoiceDisplayStatusSchema = z.enum(INVOICE_DISPLAY_STATUSES)
export type InvoiceDisplayStatus = z.infer<typeof invoiceDisplayStatusSchema>

export const INVOICE_MAX_LINES = 100
/** Largest line unit amount and quantity (thousandths): keeps every total inside a safe integer. */
export const INVOICE_MAX_UNIT_AMOUNT_MINOR = 100_000_000_000
export const INVOICE_MAX_QUANTITY_MILLI = 1_000_000_000

/** Quota error `details.reason` when the Sydney-month invoice cap is reached. */
export const INVOICE_QUOTA_REASON = "invoice_quota"

// --- Stored / response ------------------------------------------------------------------------

export const invoiceLineSchema = z.object({
  id: z.uuid(),
  invoice_id: z.uuid(),
  position: z.number().int(),
  description: z.string(),
  quantity_milli: z.number().int(),
  unit_amount_minor: z.number().int(),
  tax_code: invoiceTaxCodeSchema,
  net_minor: z.number().int(),
  gst_minor: z.number().int().default(0),
  gross_minor: z.number().int(),
})

export type InvoiceLine = z.infer<typeof invoiceLineSchema>

export const invoiceSchema = z.object({
  id: z.uuid(),
  organisation_id: z.uuid(),
  customer_id: z.uuid().nullable().default(null),
  buyer_name: z.string().nullable().default(null),
  buyer_abn: z.string().nullable().default(null),
  document_title: invoiceDocumentTitleSchema,
  status: invoiceStatusSchema,
  send_status: invoiceSendStatusSchema.default("none"),
  send_attempts: z.number().int().default(0),
  /** Address of the latest send attempt (prefills "Send again"). */
  send_to_email: z.string().nullable().default(null),
  sent_at: z.string().datetime({ offset: true }).nullable().default(null),
  voided_at: z.string().datetime({ offset: true }).nullable().default(null),
  sequence_no: z.number().int().nullable().default(null),
  number: z.string().nullable().default(null),
  issue_date: z.string().date(),
  due_date: z.string().date(),
  amounts_are: invoiceAmountsAreSchema,
  gst_rounding: invoiceGstRoundingSchema,
  currency: z.string().default("AUD"),
  subtotal_minor: z.number().int(),
  gst_minor: z.number().int().default(0),
  total_minor: z.number().int(),
  rounding_adjustment_minor: z.number().int().default(0),
  note: z.string().nullable().default(null),
  show_bank_details: z.boolean().default(false),
  pdf_storage_path: z.string().nullable().default(null),
  /** Chosen template; null = the organisation default. Lenient: stored as plain text. */
  template: z.string().nullable().default(null),
  /** Look frozen at Send / Charge & issue; null on invoices sent before T467 (Classic, no brand). */
  brand_snapshot: brandSnapshotSchema.nullable().default(null),
  /** Created by Collect payment (paid on the spot). */
  collected_on_spot: z.boolean().default(false),
  deleted_at: z.string().datetime({ offset: true }).nullable().default(null),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
})

export type Invoice = z.infer<typeof invoiceSchema>

/** Invoice with its lines, live payments and the derived amounts the page shows. */
export const invoiceDetailSchema = invoiceSchema.extend({
  lines: z.array(invoiceLineSchema).default([]),
  payments: z.array(paymentSchema).default([]),
  customer: customerSchema.nullable().default(null),
  paid_minor: z.number().int().default(0),
  outstanding_minor: z.number().int().default(0),
  display_status: invoiceDisplayStatusSchema,
})

export type InvoiceDetail = z.infer<typeof invoiceDetailSchema>

export const invoiceSendLogSchema = z.object({
  id: z.uuid(),
  invoice_id: z.uuid(),
  attempt: z.number().int(),
  outcome: invoiceSendOutcomeSchema,
  error_code: z.string().nullable().default(null),
  provider_message_id: z.string().nullable().default(null),
  to_email: z.string(),
  created_at: z.string().datetime({ offset: true }),
})

export type InvoiceSendLog = z.infer<typeof invoiceSendLogSchema>

/** `me.entitlements.invoices`: invoices created this Sydney month (drafts count). */
export const invoiceUsageSchema = z.object({
  monthlyQuota: z.number().int().nonnegative().nullable(),
  used: z.number().int().nonnegative(),
  resetsOn: z.string().date(),
})

export type InvoiceUsage = z.infer<typeof invoiceUsageSchema>

// --- Requests ---------------------------------------------------------------------------------

export const invoiceLineRequestSchema = z.strictObject({
  description: z.string().trim().min(1).max(200),
  quantity_milli: z.number().int().min(1).max(INVOICE_MAX_QUANTITY_MILLI),
  unit_amount_minor: z.number().int().min(0).max(INVOICE_MAX_UNIT_AMOUNT_MINOR),
  tax_code: invoiceTaxCodeSchema,
})

export type InvoiceLineRequest = z.infer<typeof invoiceLineRequestSchema>

export const invoiceBodyShape = {
  customer_id: z.uuid().nullable().default(null),
  /** Ad hoc buyer when no customer is chosen; a chosen customer wins at send. */
  buyer_name: z.string().trim().min(1).max(120).nullable().default(null),
  buyer_abn: abnRequestSchema.nullable().default(null),
  issue_date: z.string().date(),
  due_date: z.string().date(),
  amounts_are: invoiceAmountsAreSchema,
  gst_rounding: invoiceGstRoundingSchema,
  lines: z.array(invoiceLineRequestSchema).min(1).max(INVOICE_MAX_LINES),
  note: z.string().trim().max(1000).nullable().default(null),
  show_bank_details: z.boolean().default(false),
  template: invoiceTemplateSchema.nullable().default(null),
}

const dueNotBeforeIssue = (data: { issue_date: string; due_date: string }) =>
  data.due_date >= data.issue_date

export const createInvoiceRequestSchema = z
  .strictObject({ id: z.uuid().optional(), ...invoiceBodyShape })
  .refine(dueNotBeforeIssue, {
    message: "due_date must not be before issue_date",
    path: ["due_date"],
  })

export type CreateInvoiceRequest = z.input<typeof createInvoiceRequestSchema>
export type ParsedCreateInvoiceRequest = z.infer<typeof createInvoiceRequestSchema>

/**
 * Collect payment (R2.13): one request creates the invoice, numbers it and records the full
 * payment. The invoice is issued and due on the payment date, so there is no issue/due date.
 */
const { issue_date: _issue, due_date: _due, ...collectBodyShape } = invoiceBodyShape
export const collectPaymentRequestSchema = z.strictObject({
  id: z.uuid().optional(),
  ...collectBodyShape,
  received_on: z.string().date(),
  method: paymentMethodSchema,
  reference: z.string().trim().max(100).nullable().default(null),
})

export type CollectPaymentRequest = z.input<typeof collectPaymentRequestSchema>
export type ParsedCollectPaymentRequest = z.infer<typeof collectPaymentRequestSchema>

/** A draft is replaced as a whole: the form always posts every field. */
export const updateInvoiceRequestSchema = z
  .strictObject(invoiceBodyShape)
  .refine(dueNotBeforeIssue, {
    message: "due_date must not be before issue_date",
    path: ["due_date"],
  })

export type UpdateInvoiceRequest = z.input<typeof updateInvoiceRequestSchema>
export type ParsedUpdateInvoiceRequest = z.infer<typeof updateInvoiceRequestSchema>

/** Seller profile on the organisation (the ABN, GST status and bank details printed on invoices). */
export const sellerProfileSchema = z.object({
  business_name: z.string().nullable().default(null),
  abn: z.string().nullable().default(null),
  gst_registered: z.boolean().default(false),
  address_line1: z.string().nullable().default(null),
  address_line2: z.string().nullable().default(null),
  suburb: z.string().nullable().default(null),
  state: z.string().nullable().default(null),
  postcode: z.string().nullable().default(null),
  bank_account_name: z.string().nullable().default(null),
  bank_bsb: z.string().nullable().default(null),
  bank_account_number: z.string().nullable().default(null),
  invoice_prefix: z.string().default("INV-"),
  default_payment_terms_days: z.number().int().default(14),
})

export type SellerProfile = z.infer<typeof sellerProfileSchema>

const nullableText = (max: number) => z.string().trim().max(max).nullable()

/** Field validators of the seller profile form; the sync patch reuses them without defaults. */
export const sellerProfileFieldValidators = {
  business_name: z.string().trim().min(1).max(120).nullable(),
  abn: abnRequestSchema.nullable(),
  gst_registered: z.boolean(),
  address_line1: nullableText(120),
  address_line2: nullableText(120),
  suburb: nullableText(60),
  state: nullableText(3),
  postcode: z
    .string()
    .trim()
    .regex(/^\d{4}$/, "postcode must be 4 digits")
    .nullable(),
  bank_account_name: nullableText(60),
  bank_bsb: z
    .string()
    .trim()
    .regex(/^\d{3}-?\d{3}$/, "bsb must be 6 digits")
    .nullable(),
  bank_account_number: z
    .string()
    .trim()
    .regex(/^\d{5,9}$/, "account number must be 5 to 9 digits")
    .nullable(),
  invoice_prefix: z
    .string()
    .trim()
    .min(1)
    .max(10)
    .regex(/^[A-Za-z0-9-]+$/),
  default_payment_terms_days: z.number().int().min(0).max(365),
}

const v = sellerProfileFieldValidators
export const updateSellerProfileRequestSchema = z.strictObject({
  business_name: v.business_name.default(null),
  abn: v.abn.default(null),
  gst_registered: v.gst_registered.default(false),
  address_line1: v.address_line1.default(null),
  address_line2: v.address_line2.default(null),
  suburb: v.suburb.default(null),
  state: v.state.default(null),
  postcode: v.postcode.default(null),
  bank_account_name: v.bank_account_name.default(null),
  bank_bsb: v.bank_bsb.default(null),
  bank_account_number: v.bank_account_number.default(null),
  invoice_prefix: v.invoice_prefix.default("INV-"),
  default_payment_terms_days: v.default_payment_terms_days.default(14),
})

export type UpdateSellerProfileRequest = z.input<typeof updateSellerProfileRequestSchema>
export type ParsedUpdateSellerProfileRequest = z.infer<typeof updateSellerProfileRequestSchema>

/** Issues that stop Send (`VALIDATION_FAILED` details.issues). */
export const invoiceIssueCodeSchema = z.enum([
  "BUYER_IDENTITY_REQUIRED",
  "SELLER_NAME_REQUIRED",
  "SELLER_ABN_REQUIRED",
  "NO_LINES",
])
